<script>
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import countries from 'shared/constants/countries.js';
import { isPhoneNumberValid } from 'shared/helpers/Validators';
import parsePhoneNumber from 'libphonenumber-js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';

export default {
  components: {
    NextButton,
    Avatar,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    channelType: {
      type: String,
      default: '',
    },
    inProgress: {
      type: Boolean,
      default: false,
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['cancel', 'success'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      countries: countries,
      companyName: '',
      description: '',
      email: '',
      name: '',
      phoneNumber: '',
      activeDialCode: '+66',
      avatarFile: null,
      avatarUrl: '',
      country: {
        id: '',
        name: '',
      },
      city: '',
      socialProfileUserNames: {
        facebook: '',
        twitter: '',
        linkedin: '',
        github: '',
      },
      socialProfileKeys: [
        { key: 'facebook', prefixURL: 'https://facebook.com/' },
        { key: 'twitter', prefixURL: 'https://twitter.com/' },
        { key: 'linkedin', prefixURL: 'https://linkedin.com/' },
        { key: 'github', prefixURL: 'https://github.com/' },
      ],
      usernameBo: '',
      vip: '',
      vipOptions: [
        { value: '', label: 'None' },
        ...Array.from({ length: 50 }, (_, i) => ({
          value: `VIP${i + 1}`,
          label: `VIP${i + 1}`,
        })),
      ],
    };
  },
  validations: {
    name: {
      required,
    },
    description: {},
    email: {
      email,
    },
    companyName: {},
    phoneNumber: {},
    bio: {},
  },
  computed: {
    parsePhoneNumber() {
      return parsePhoneNumber(this.phoneNumber);
    },
    isPhoneNumberNotValid() {
      if (this.phoneNumber !== '') {
        return (
          !isPhoneNumberValid(this.phoneNumber, this.activeDialCode) ||
          (this.phoneNumber !== '' ? this.activeDialCode === '' : false)
        );
      }
      return false;
    },
    phoneNumberError() {
      if (this.activeDialCode === '') {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DIAL_CODE_ERROR');
      }
      if (!isPhoneNumberValid(this.phoneNumber, this.activeDialCode)) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR');
      }
      return '';
    },
    setPhoneNumber() {
      if (this.parsePhoneNumber && this.parsePhoneNumber.countryCallingCode) {
        return this.phoneNumber;
      }
      if (this.phoneNumber === '' && this.activeDialCode !== '') {
        return '';
      }
      return this.activeDialCode
        ? `${this.activeDialCode}${this.phoneNumber}`
        : '';
    },
    isLiveChatConversation() {
      const channel = String(this.channelType || '').toLowerCase();
      return channel.includes('webwidget') || channel.includes('web_widget');
    },
    hasNameChanged() {
      return this.name.trim() !== String(this.contact.name || '').trim();
    },
  },
  watch: {
    contact() {
      this.setContactObject();
    },
  },
  mounted() {
    this.setContactObject();
    this.setDialCode();
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    onSuccess() {
      this.$emit('success');
    },
    countryNameWithCode({ name, id }) {
      if (!id) return name;
      if (!name && !id) return '';
      return `${name} (${id})`;
    },
    setDialCode() {
      if (
        this.phoneNumber !== '' &&
        this.parsePhoneNumber &&
        this.parsePhoneNumber.countryCallingCode
      ) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        this.activeDialCode = `+${dialCode}`;
      } else {
        // Default to Thailand dial code (+66)
        this.activeDialCode = '+66';
      }
    },
    setContactObject() {
      const {
        email: emailAddress,
        phone_number: phoneNumber,
        name,
      } = this.contact;
      const additionalAttributes = this.contact.additional_attributes || {};

      this.name = name || '';
      this.email = emailAddress || '';
      this.phoneNumber = phoneNumber || '';
      this.companyName = additionalAttributes.company_name || '';
      this.country = {
        id: additionalAttributes.country_code || '',
        name:
          additionalAttributes.country ||
          this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY'),
      };
      this.city = additionalAttributes.city || '';
      this.description = additionalAttributes.description || '';
      this.avatarUrl = this.contact.thumbnail || '';
      const {
        social_profiles: socialProfiles = {},
        screen_name: twitterScreenName,
      } = additionalAttributes;
      this.socialProfileUserNames = {
        twitter: socialProfiles.twitter || twitterScreenName || '',
        facebook: socialProfiles.facebook || '',
        linkedin: socialProfiles.linkedin || '',
        github: socialProfiles.github || '',
        instagram: socialProfiles.instagram || '',
      };
      const customAttributes = this.contact.custom_attributes || {};
      this.usernameBo = customAttributes.username_bo || '';
      this.vip = customAttributes.vip || '';
    },
    getContactObject() {
      if (this.country === null) {
        this.country = {
          id: '',
          name: '',
        };
      }
      const contactObject = {
        id: this.contact.id,
        name: this.name,
        email: this.email,
        phone_number: this.setPhoneNumber,
        additional_attributes: {
          ...this.contact.additional_attributes,
          description: this.description,
          company_name: this.companyName,
          country_code: this.country.id,
          country:
            this.country.name ===
            this.$t('CONTACT_FORM.FORM.COUNTRY.SELECT_COUNTRY')
              ? ''
              : this.country.name,
          city: this.city,
          social_profiles: this.socialProfileUserNames,
        },
        custom_attributes: {
          ...this.contact.custom_attributes,
          username_bo: this.usernameBo,
          vip: this.vip,
        },
      };
      if (this.avatarFile) {
        contactObject.avatar = this.avatarFile;
        contactObject.isFormData = true;
      }
      if (this.isLiveChatConversation && this.hasNameChanged) {
        contactObject.lockLivechatName = true;
      }
      return contactObject;
    },
    setPhoneCode(code) {
      if (this.phoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        if (dialCode === code) {
          return;
        }
        this.activeDialCode = `+${dialCode}`;
        const newPhoneNumber = this.phoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );
        this.phoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
    async handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid || this.isPhoneNumberNotValid) {
        return;
      }
      try {
        await this.onSubmit(this.getContactObject());
        this.onSuccess();
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          if (error.data.includes('email')) {
            useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
          } else if (error.data.includes('phone_number')) {
            useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        if (this.contact && this.contact.id) {
          await this.$store.dispatch('contacts/deleteAvatar', this.contact.id);
          useAlert(this.$t('CONTACT_FORM.DELETE_AVATAR.API.SUCCESS_MESSAGE'));
        }
        this.avatarFile = null;
        this.avatarUrl = '';
        this.activeDialCode = '';
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('CONTACT_FORM.DELETE_AVATAR.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <form
    class="w-full h-full min-h-full flex flex-col contact--form"
    @submit.prevent="handleSubmit"
  >
    <div class="contact-form__fields">
      <div class="wlab-form-field">
      <label class="wlab-form-label">
        {{ $t('CONTACT_FORM.FORM.AVATAR.LABEL') }}
      </label>
      <Avatar
        :src="avatarUrl"
        :size="72"
        :name="contact.name"
        allow-upload
        rounded-full
        @upload="handleImageUpload"
        @delete="handleAvatarDelete"
      />
    </div>
      <div class="wlab-form-field">
      <label class="wlab-form-label">
        {{ $t('CONTACT_PANEL.BO_USERNAME') }}
      </label>
      <input
        v-model="usernameBo"
        type="text"
        :placeholder="$t('CONTACT_FORM.FORM.USERNAME_BO.PLACEHOLDER')"
        class="wlab-form-input"
      />
    </div>
      <div class="wlab-form-field">
      <label class="wlab-form-label">
        {{ $t('CONVERSATION_SIDEBAR.VIP_LEVEL') }}
      </label>
      <select v-model="vip" class="wlab-form-input">
        <option
          v-for="option in vipOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>
      <div class="wlab-form-field">
      <label
        class="wlab-form-label"
        :class="{ 'text-n-ruby-12': v$.name.$error }"
      >
        {{ $t('CONTACT_FORM.FORM.NAME.LABEL') }}
      </label>
      <input
        v-model="name"
        type="text"
        :placeholder="$t('CONTACT_FORM.FORM.NAME.PLACEHOLDER')"
        class="wlab-form-input"
        :class="{ 'wlab-form-input--error': v$.name.$error }"
        @input="v$.name.$touch"
      />
    </div>
      <div class="wlab-form-field">
      <label
        class="wlab-form-label"
        :class="{ 'text-n-ruby-12': v$.email.$error }"
      >
        {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}
      </label>
      <input
        v-model="email"
        type="text"
        :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
        class="wlab-form-input"
        :class="{ 'wlab-form-input--error': v$.email.$error }"
        @input="v$.email.$touch"
      />
      <p v-if="v$.email.$error" class="wlab-form-error">
        {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
      </p>
    </div>
      <div class="wlab-form-field">
      <label
        class="wlab-form-label"
        :class="{ 'text-n-ruby-12': v$.description.$error }"
      >
        {{ $t('CONTACT_FORM.FORM.BIO.LABEL') }}
      </label>
      <textarea
        v-model="description"
        :placeholder="$t('CONTACT_FORM.FORM.BIO.PLACEHOLDER')"
        class="wlab-form-input wlab-form-textarea"
        :class="{ 'wlab-form-input--error': v$.description.$error }"
        @input="v$.description.$touch"
      />
    </div>
      <div class="wlab-form-field">
      <label
        class="wlab-form-label"
        :class="{ 'text-n-ruby-12': isPhoneNumberNotValid }"
      >
        {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') }}
      </label>
      <woot-phone-input
        v-model="phoneNumber"
        :value="phoneNumber"
        :error="isPhoneNumberNotValid"
        :placeholder="$t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')"
        @blur="v$.phoneNumber.$touch"
        @set-code="setPhoneCode"
      />
      <p v-if="isPhoneNumberNotValid" class="wlab-form-error">
        {{ phoneNumberError }}
      </p>
      <div
        v-if="isPhoneNumberNotValid || !phoneNumber"
        class="relative mx-0 mt-2 p-2 rounded-md border border-solid border-n-amber-5 text-n-amber-12 bg-n-amber-3"
      >
        {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.HELP') }}
      </div>
    </div>
      <div class="wlab-form-field">
      <label class="wlab-form-label">
        {{ $t('CONTACT_FORM.FORM.COMPANY_NAME.LABEL') }}
      </label>
      <input
        v-model="companyName"
        type="text"
        :placeholder="$t('CONTACT_FORM.FORM.COMPANY_NAME.PLACEHOLDER')"
        class="wlab-form-input"
      />
    </div>
      <div class="wlab-form-field">
      <label class="wlab-form-label">
        {{ $t('CONTACT_FORM.FORM.COUNTRY.LABEL') }}
      </label>
      <multiselect
        v-model="country"
        track-by="id"
        label="name"
        :placeholder="$t('CONTACT_FORM.FORM.COUNTRY.PLACEHOLDER')"
        selected-label
        :select-label="$t('CONTACT_FORM.FORM.COUNTRY.SELECT_PLACEHOLDER')"
        :deselect-label="$t('CONTACT_FORM.FORM.COUNTRY.REMOVE')"
        :custom-label="countryNameWithCode"
        :max-height="160"
        :options="countries"
        allow-empty
        :option-height="104"
      />
    </div>
      <div class="wlab-form-field">
      <label class="wlab-form-label">
        {{ $t('CONTACT_FORM.FORM.CITY.LABEL') }}
      </label>
      <input
        v-model="city"
        type="text"
        :placeholder="$t('CONTACT_FORM.FORM.CITY.PLACEHOLDER')"
        class="wlab-form-input"
      />
    </div>

      <div class="wlab-form-field">
      <label class="wlab-form-label">
        {{ $t('CONTACTS_PAGE.LIST.TABLE_HEADER.SOCIAL_PROFILES') }}
      </label>
      <div
        v-for="socialProfile in socialProfileKeys"
        :key="socialProfile.key"
        class="flex items-stretch w-full mb-3"
      >
        <span
          class="flex items-center h-10 px-3 border-solid border-y ltr:border-l rtl:border-r ltr:rounded-l-xl rtl:rounded-r-xl bg-n-solid-3 text-n-slate-11 border-n-weak text-[13px]"
        >
          {{ socialProfile.prefixURL }}
        </span>
        <input
          v-model="socialProfileUserNames[socialProfile.key]"
          class="flex-1 px-3 py-2 border border-n-weak ltr:rounded-l-none ltr:rounded-r-xl rtl:rounded-r-none rtl:rounded-l-xl bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand mb-0 text-[14px]"
          type="text"
        />
      </div>
      </div>
    </div>
    <div class="contact-form__footer">
      <NextButton
        faded
        slate
        type="reset"
        :label="$t('CONTACT_FORM.FORM.CANCEL')"
        class="contact-form__cancel-btn"
        @click.prevent="onCancel"
      />
      <NextButton
        blue
        type="submit"
        :label="$t('CONTACT_FORM.FORM.SUBMIT')"
        :is-loading="inProgress"
        class="wlab-form-submit contact-form__submit-btn"
      />
    </div>
  </form>
</template>

<style scoped lang="scss">
.contact--form {
  min-height: 0;
}

.contact-form__fields {
  flex: 1 1 auto;
  min-height: 0;
  padding-bottom: 16px;
}

.contact-form__footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
  padding-top: 12px;
  margin-top: auto;
  flex-shrink: 0;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
}

:global(body.dark:not(.theme-mix)) .contact-form__footer {
  background: #202024;
  border-top-color: rgba(255, 255, 255, 0.12);
}

:global(body.theme-mix) .contact-form__footer {
  background: #ffffff;
  border-top-color: #e5e7eb;
}

:global(body.dark:not(.theme-mix)) .contact-form__cancel-btn {
  background: #2a2a2e !important;
  color: #f6f6f7 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

:global(body.dark:not(.theme-mix))
  .contact-form__cancel-btn:hover:not(:disabled) {
  background: #34343a !important;
  color: #ffffff !important;
}

:global(body.theme-mix) .contact-form__cancel-btn {
  background: #ffffff !important;
  color: #131317 !important;
  border-color: #d1d5db !important;
}

:global(body.theme-mix) .contact-form__cancel-btn:hover:not(:disabled) {
  background: #f8fafc !important;
  color: #111827 !important;
}

::v-deep {
  .multiselect {
    min-height: 40px;
  }

  .multiselect .multiselect__tags {
    min-height: 40px;
    border: 1px solid #d5d5d6;
    border-radius: 12px;
    background: #f3f4f6;
    padding: 8px 12px;
    font-size: 14px;
  }

  .multiselect .multiselect__tags .multiselect__single,
  .multiselect .multiselect__input,
  .multiselect .multiselect__placeholder,
  .multiselect .multiselect__option,
  .multiselect .multiselect__content-wrapper .multiselect__option {
    font-size: 14px !important;
  }

  .multiselect .multiselect__select {
    top: 9px;
  }

  .phone-input--wrap {
    input,
    select {
      height: 40px !important;
      border-radius: 12px !important;
      font-size: 14px !important;
    }
  }
}

:global(.dark) {
  ::v-deep .multiselect .multiselect__tags {
    background: #202024;
    border-color: rgba(255, 255, 255, 0.12);
    color: #f6f6f7;
  }
  ::v-deep .phone-input--wrap input,
  ::v-deep .phone-input--wrap select {
    background: #202024;
    border-color: rgba(255, 255, 255, 0.12);
    color: #f6f6f7;
  }
}
</style>
